import { createStore } from 'solid-js/store'
import type { RecorderState } from '@models/recorder'

export type Tag = {
	id: string
	label: string
	level: number
	icon_name: string
}

export type Book = {
	logbook_ref: string
	name: string
	description?: string
	enabled_tag_ids?: string[],
	allow_date_selection?: boolean
	allow_text_search?: boolean
}

export type User = {
	id?: string
	user_ref?: string
	name?: string
	display_name?: string
	email?: string
	[key: string]: any
	staff?: {
		email: string,
		staff_ref: string,
		name: string
		role: string
	},
	capabilities?: {
		is_manager: boolean
		is_owner: boolean
	}
}

type RefStore = {
	tags: Tag[]
	books: Book[]
	templates: { template: string; name: string; logbook: string; initial_state?: any; auto_submit?: boolean }[]
	sessionUser: User | null
	tenant_slug?: string
	venue_slug?: string
}

const [store, setStore] = createStore<RefStore>({ tags: [], books: [], templates: [], sessionUser: null, tenant_slug: '', venue_slug: '' })

export const getTags = () => store.tags

export const setTags = (tags: Tag[]) => setStore('tags', tags)

export const addTag = (tag: Tag) => setStore('tags', tags => [...tags, tag])

export const getTemplates = () => store.templates

export const setTemplates = (templates: { template: string; name: string; logbook: string; initial_state?: any; auto_submit?: boolean }[]) => setStore('templates', templates)

export const addTemplate = (template: { template: string; name: string; logbook: string; initial_state?: any; auto_submit?: boolean }) => setStore('templates', templates => [...templates, template])

export const updateTag = (id: string, patch: Partial<Tag>) => {
	setStore('tags', tags => tags.map(t => t.id === id ? { ...t, ...patch } : t))
}

export const removeTag = (id: string) => setStore('tags', tags => tags.filter(t => t.id !== id))

export const getBooks = () => store.books

export const setBooks = (books: Book[]) => setStore('books', books)

export const getSessionUser = () => store.sessionUser

export const setSessionUser = (u: User | null) => setStore('sessionUser', u)

export const getTenantSlug = () => store.tenant_slug

export const getVenueSlug = () => store.venue_slug

export const setTenantSlug = (s: string) => setStore('tenant_slug', s)

export const setVenueSlug = (s: string) => setStore('venue_slug', s)

export const addBook = (book: Book) => setStore('books', books => [...books, book])

export const updateBook = (id: string, patch: Partial<Book>) => {
	setStore('books', books => books.map(b => b.logbook_ref === id ? { ...b, ...patch } : b))
}

export const removeBook = (id: string) => setStore('books', books => books.filter(b => b.logbook_ref !== id))

// Recorder state persistence store
const RECORDER_STATE_KEY = 'recorder_state'

const defaultRecorderState = (): RecorderState => ({
	cached_data: { selected_book_name: '', selected_repoter_name: '' },
	occured_at: new Date(),
	incident_details: { what_happened: '', details_of_third_parties: '', staff_actions: '', known_individuals: false },
	security_details: { police_involved: false, police_incident_number: '' },
	selected_book_id: '',
	selected_repoter_id: '',
	selected_tags: []
})

const [recorderState, setRecorderStateStore] = createStore<RecorderState>(defaultRecorderState())

const parseRecorderState = (raw: string): RecorderState => {
	try {
		const parsed = JSON.parse(raw) as RecorderState
		return {
			...parsed,
			occured_at: parsed.occured_at ? new Date(parsed.occured_at) : new Date()
		}
	} catch {
		return defaultRecorderState()
	}
}

const writeRecorderState = (state: RecorderState) => {
	try {
		localStorage.setItem(RECORDER_STATE_KEY, JSON.stringify({ ...state, occured_at: state.occured_at.toISOString() }))
	} catch (_e) {
		// ignore storage write errors
	}
}

export const loadRecorderState = () => {
	try {
		const raw = localStorage.getItem(RECORDER_STATE_KEY)
		if (raw) {
			setRecorderStateStore(parseRecorderState(raw))
		} else {
			setRecorderStateStore(defaultRecorderState())
		}
	} catch (_e) {
		setRecorderStateStore(defaultRecorderState())
	}
}

export const getRecorderState = () => recorderState

export const setRecorderState = (next: RecorderState) => {
	setRecorderStateStore(next)
	writeRecorderState(next)
}

export const updateRecorderState = (patch: Partial<RecorderState>) => {
	setRecorderStateStore(patch)
	writeRecorderState(recorderState)
}

export const resetRecorderState = () => {
	const reset = defaultRecorderState()
	setRecorderStateStore(reset)
	try {
		localStorage.removeItem(RECORDER_STATE_KEY)
	} catch (_e) {
		// ignore
	}
}

loadRecorderState()

export default store
